import { useContext, useEffect, useState } from "react";
import Message from "../../components/ui/Message";
import { PriceChangeSettings } from "./PriceChangeSettings";
import { VoucherLogoSettings } from "./VoucherLogoSettings";
import { EcommerceTokenSettings } from "./EcommerceTokenSettings";
import { AuthContext } from "../../context/auth";
import { MerchantsContext } from "../../context/merchants/MerchantContext";
import { MerchantAccountsContext } from "../../context/merchantAccounts/MerchantAccountContext";
import { COMPANY_TYPE_RESTAURANT } from '../../utils/companyType';
import { SWIFTPAY_BANK_ID } from "../../utils/appConfig";

const COUNTRY_COSTA_RICA = 1;

// Sin país se toma Costa Rica (comercios anteriores a Panamá)
const isCostaRicaMerchant = (merchant: any) =>
    (Number(merchant?.country_id) || COUNTRY_COSTA_RICA) === COUNTRY_COSTA_RICA;

// Cuentas de liquidación: solo comercios de Swiftpay en Costa Rica
const showsBankAccounts = (merchant: any) =>
    isCostaRicaMerchant(merchant) && Number(merchant?.bankId) === SWIFTPAY_BANK_ID;

const currencyConfig: Record<number, { label: string; short: string; badgeClass: string }> = {
    188: { label: 'Cuenta en colones', short: 'CRC', badgeClass: 'bg-primary-subtle text-primary-emphasis' },
    840: { label: 'Cuenta en dólares', short: 'USD', badgeClass: 'bg-success-subtle text-success-emphasis' },
};

const getCompanyType = (companyTypeId: number) => {
    switch (companyTypeId) {
        case 1:
            return "Restaurante";
        case 2:
            return "Comercio";
        case 3:
            return "Delivery";
        case 4:
            return "POS";
        default:
            return "";
    }
}

type TabKey = 'general' | 'settings' | 'voucher' | 'prices' | 'ecommerce' | 'accounts';

// Mi empresa: datos del comercio y, por pestañas, su configuración (régimen simplificado solo Costa Rica, servicio a
// la mesa solo restaurantes), el logo del voucher impreso, los cambios de precio en caja y las cuentas bancarias donde
// se liquidan sus pagos (solo Swiftpay en Costa Rica)
export const MerchantTopPage = () => {

    const { user } = useContext(AuthContext);
    const { getCurrentMerchant, loadMerchantIdTypes, configMerchant } = useContext(MerchantsContext);
    const { loadMerchantAccounts, merchantAccounts, loadCatalogs, banks, accountTypes } = useContext(MerchantAccountsContext);

    const [merchant, setMerchant] = useState<any>(null);
    const [idTypes, setIdTypes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [copiedIban, setCopiedIban] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>('general');

    // Configuración: valores guardados y del formulario (para saber si hay cambios)
    const [saved, setSaved] = useState({ simplifiedRegime: false, tableService: false });
    const [simplifiedRegime, setSimplifiedRegime] = useState(false);
    const [tableService, setTableService] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const loadData = async () => {
        setIsLoading(true);
        setLoadError(false);
        try {
            // Catálogos y comercio en paralelo: si falla un catálogo la pantalla igual se muestra
            const current = await getCurrentMerchant();
            // Tipos de identificación del país del comercio
            const types = await loadMerchantIdTypes((current as any)?.country_id).catch(() => []);
            setIdTypes(types ?? []);

            if (!current) {
                setLoadError(true);
                return;
            }
            setMerchant(current);

            const initial = {
                simplifiedRegime: !!current.simplifiedRegime,
                tableService: current.companyTypeId == COMPANY_TYPE_RESTAURANT ? !!current.tableService : false,
            };
            setSaved(initial);
            setSimplifiedRegime(initial.simplifiedRegime);
            setTableService(initial.tableService);

            // Si fallan las cuentas o sus catálogos, los datos de la empresa igual se muestran
            if (showsBankAccounts(current)) {
                await Promise.all([
                    loadCatalogs().catch(() => undefined),
                    loadMerchantAccounts(current.id, 1).catch(() => undefined),
                ]);
            }
        } catch (e) {
            setLoadError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const getIdTypeName = (idType: number) =>
        idTypes.find((t: any) => t.id == idType)?.description ?? "-";

    const getBankName = (bankCode: number) =>
        banks.find((b: any) => b.code == bankCode)?.description ?? "-";

    const getAccountTypeName = (accountTypeId: number) =>
        accountTypes?.find((t: any) => t.code == accountTypeId)?.description
        ?? (accountTypeId == 1 ? "Ahorros" : accountTypeId == 2 ? "Corriente" : "-");

    const handleCopy = async (iban: string) => {
        try {
            await navigator.clipboard.writeText(iban);
            setCopiedIban(iban);
            setTimeout(() => setCopiedIban(null), 1500);
        } catch (e) { }
    };

    const hasChanges = simplifiedRegime !== saved.simplifiedRegime || tableService !== saved.tableService;

    const handleSave = async () => {
        setIsSaving(true);
        const resp = await configMerchant({
            id: merchant.id,
            simplifiedRegime,
            tableService,
        });
        setIsSaving(false);

        if (resp == "success") {
            // La sesión también la usan otras pantallas (facturación, menú QR)
            if (user?.merchant) {
                user.merchant.simplifiedRegime = simplifiedRegime;
                user.merchant.tableService = tableService;
            }
            setSaved({ simplifiedRegime, tableService });
            setSuccessMessage("Configuración actualizada correctamente");
        } else {
            setErrorMessage(resp);
        }
    }

    const handleReset = () => {
        setSimplifiedRegime(saved.simplifiedRegime);
        setTableService(saved.tableService);
    }

    if (isLoading) {
        return (
            <div className="mt-5 container-fluid px-4 text-center py-5">
                <span className="spinner-border spinner-border-sm me-2" /> Cargando información de la empresa...
            </div>
        );
    }

    if (loadError || !merchant) {
        return (
            <div className="mt-5 container-fluid px-4" style={styles.container}>
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: 32 }} />
                        <h6 className="mt-2 mb-1">No se pudo cargar la información de la empresa</h6>
                        <p className="text-muted small mb-3">Verifique su conexión e intente de nuevo.</p>
                        <button type="button" className="btn btn-primary" onClick={loadData}>
                            <i className="bi bi-arrow-clockwise me-1" /> Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const isCostaRica = isCostaRicaMerchant(merchant);
    const hasBankAccounts = showsBankAccounts(merchant);
    const isRestaurant = merchant.companyTypeId == COMPANY_TYPE_RESTAURANT;
    const hasSettings = isCostaRica || isRestaurant;
    const companyType = getCompanyType(merchant.companyTypeId);

    const infoFields = [
        { label: 'Identificación legal', value: merchant.legalId, icon: 'bi-person-vcard' },
        { label: 'Tipo de identificación', value: getIdTypeName(merchant.typeLegalId), icon: 'bi-card-text' },
        { label: 'Correo electrónico', value: merchant.paymentEmail || merchant.email, icon: 'bi-envelope' },
        { label: 'Teléfono', value: merchant.phone, icon: 'bi-telephone' },
        { label: 'Dirección', value: merchant.address, icon: 'bi-geo-alt', wide: true },
    ];

    const tabs: { key: TabKey; label: string; icon: string }[] = [
        { key: 'general', label: 'Datos generales', icon: 'bi-building' },
        ...(hasSettings ? [{ key: 'settings' as TabKey, label: 'Configuración', icon: 'bi-sliders' }] : []),
        { key: 'voucher', label: 'Voucher impreso', icon: 'bi-printer' },
        { key: 'prices', label: 'Cambios de precio', icon: 'bi-tag' },
        { key: 'ecommerce', label: 'Ecommerce', icon: 'bi-key' },
        ...(hasBankAccounts ? [{ key: 'accounts' as TabKey, label: 'Cuentas bancarias', icon: 'bi-bank' }] : []),
    ];
    const currentTab = tabs.some(t => t.key === activeTab) ? activeTab : 'general';
    // Logo del voucher o, si no hay, la imagen del comercio (ruta del API)
    const logo = merchant.printLogoImage
        || (merchant.image ? `${process.env.REACT_APP_IMAGES_URL ?? ''}${merchant.image}` : null);

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            {/* Encabezado: identidad del comercio */}
            <div className="card border-0 shadow-sm mb-3 overflow-hidden">
                <div style={styles.banner} />
                <div className="card-body pt-0 px-4 pb-4">
                    {/* Solo el logo se monta sobre la franja; el texto queda en la parte blanca */}
                    <div style={{ ...styles.avatar, marginTop: -40 }}>
                        {logo
                            ? <img src={logo} alt={`Logo de ${merchant.name}`} style={styles.avatarImg} />
                            : <i className="bi bi-building" />}
                    </div>
                    <div className="mt-3">
                        <div>
                            <h4 className="mb-2">{merchant.name}</h4>
                            <div className="d-flex flex-wrap gap-2">
                                {companyType && <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis">{companyType}</span>}
                                <span className="badge rounded-pill bg-light text-muted border">{isCostaRica ? 'Costa Rica' : 'Panamá'}</span>
                                {merchant.legalId && <span className="badge rounded-pill bg-light text-muted border"><i className="bi bi-person-vcard me-1" />{merchant.legalId}</span>}
                                {merchant.printLogo && <span className="badge rounded-pill bg-success-subtle text-success-emphasis"><i className="bi bi-printer me-1" />Logo en voucher</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Secciones */}
            <ul className="nav nav-pills flex-nowrap overflow-auto gap-1 mb-3 pb-1" role="tablist">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.key} role="presentation">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={currentTab === tab.key}
                            className={`nav-link text-nowrap ${currentTab === tab.key ? 'active' : 'text-body'}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <i className={`bi ${tab.icon} me-1`} />{tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            <div>
                {currentTab === 'general' && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="mb-3">Datos generales</h6>
                            <div className="row g-3">
                                {infoFields.map(field => (
                                    <div key={field.label} className={field.wide ? 'col-12' : 'col-12 col-md-6'}>
                                        <div className="border rounded p-3 h-100 d-flex gap-3">
                                            <i className={`bi ${field.icon} text-primary mt-1`} />
                                            <div className="min-w-0">
                                                <div className="text-muted small">{field.label}</div>
                                                <div className="fw-medium text-break">{field.value || '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-muted small mt-3">
                                <i className="bi bi-info-circle me-1" />Para cambiar estos datos comuníquese con soporte.
                            </div>
                        </div>
                    </div>
                )}

                {/* Configuración */}
                {currentTab === 'settings' && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="mb-3">Configuración</h6>

                            {isCostaRica && (
                                <div className="border rounded p-3 mb-3">
                                    <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-0">
                                        <label className="form-check-label" htmlFor="regimeSwitch">
                                            <div className="fw-medium">Régimen simplificado</div>
                                            <div className="text-muted small">Indique si el comercio está inscrito en el régimen de tributación simplificada.</div>
                                        </label>
                                        <input
                                            id="regimeSwitch"
                                            className="form-check-input ms-3 flex-shrink-0"
                                            type="checkbox"
                                            role="switch"
                                            style={styles.switch}
                                            checked={simplifiedRegime}
                                            onChange={(e) => setSimplifiedRegime(e.target.checked)}
                                        />
                                    </div>
                                </div>
                            )}

                            {isRestaurant && (
                                <div className="border rounded p-3 mb-3">
                                    <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-0">
                                        <label className="form-check-label" htmlFor="tableServiceSwitch">
                                            <div className="fw-medium">Servicio a la mesa</div>
                                            <div className="text-muted small">Active esta opción si el restaurante atiende a sus clientes en la mesa.</div>
                                        </label>
                                        <input
                                            id="tableServiceSwitch"
                                            className="form-check-input ms-3 flex-shrink-0"
                                            type="checkbox"
                                            role="switch"
                                            style={styles.switch}
                                            checked={tableService}
                                            onChange={(e) => setTableService(e.target.checked)}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-end align-items-center gap-2 pt-1">
                                {hasChanges && <span className="text-muted small me-auto"><i className="bi bi-info-circle me-1" />Tiene cambios sin guardar.</span>}
                                <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={!hasChanges || isSaving}>
                                    Descartar
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                                    {isSaving
                                        ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</>
                                        : <><i className="bi bi-check-lg me-1" /> Guardar cambios</>}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Logo de la empresa en el voucher impreso (todos los comercios) */}
                {currentTab === 'voucher' && (
                    <VoucherLogoSettings
                        merchant={merchant}
                        configMerchant={configMerchant}
                        onSaved={(settings) => {
                            setMerchant({ ...merchant, ...settings });
                            // La sesión la usan otras pantallas
                            if (user?.merchant) user.merchant.printLogo = settings.printLogo;
                        }}
                    />
                )}

                {/* Cambios de precio en caja (todos los comercios) */}
                {currentTab === 'prices' && <PriceChangeSettings merchant={merchant} configMerchant={configMerchant} />}

                {/* Token de integración ecommerce: se ve una sola vez (al cambiar de pestaña también se descarta) */}
                {currentTab === 'ecommerce' && <EcommerceTokenSettings />}

                {/* Cuentas bancarias */}
                {currentTab === 'accounts' && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Cuentas bancarias</h6>
                                <span className="badge rounded-pill bg-light text-muted border">{merchantAccounts?.length ?? 0}</span>
                            </div>

                            {merchantAccounts && merchantAccounts.length > 0 ? (
                                <div className="row g-3">
                                    {merchantAccounts.map((account: any) => {
                                        const currency = currencyConfig[account.currencyId] ?? { label: 'Cuenta', short: String(account.currencyId), badgeClass: 'bg-secondary-subtle text-secondary-emphasis' };
                                        return (
                                            <div key={`${account.merchantId}-${account.currencyId}`} className="col-12 col-xl-6">
                                                <div className="border rounded p-3 h-100">
                                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bi bi-bank text-primary" />
                                                            <span className="fw-semibold">{currency.label}</span>
                                                            <span className={`badge rounded-pill ${currency.badgeClass}`}>{currency.short}</span>
                                                        </div>
                                                        <span className="text-muted small">{getAccountTypeName(account.accountTypeId)}</span>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <span className="fw-medium text-break" style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>
                                                            {account.ibanAccount}
                                                        </span>
                                                        {account.ibanAccount && (
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${copiedIban === account.ibanAccount ? 'btn-success' : 'btn-outline-secondary'}`}
                                                                title="Copiar IBAN"
                                                                onClick={() => handleCopy(account.ibanAccount)}
                                                            >
                                                                <i className={`bi ${copiedIban === account.ibanAccount ? 'bi-check-lg' : 'bi-clipboard'}`} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="row small">
                                                        <div className="col-sm-6">
                                                            <span className="text-muted">Banco: </span>{getBankName(account.bankCode)}
                                                        </div>
                                                        <div className="col-sm-6">
                                                            <span className="text-muted">Nombre: </span>{account.accountName || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="bi bi-bank text-muted" style={{ fontSize: 28 }} />
                                    <div className="fw-medium mt-2">No hay cuentas bancarias configuradas</div>
                                    <div className="text-muted small">Este comercio aún no tiene cuentas registradas.</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div style={{ height: '60px' }}></div>

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
            {successMessage.length > 0 && (
                <Message type='success' message={successMessage} title='Exitoso' closeDialog={() => setSuccessMessage('')} handleOk={() => setSuccessMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
        </div>
    );
};

const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    banner: {
        height: 72,
        background: 'linear-gradient(90deg, #e7f1ff 0%, #cfe2ff 100%)',
    },
    avatar: {
        width: 80,
        height: 80,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 32,
        backgroundColor: '#fff',
        color: '#0d6efd',
        border: '3px solid #fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        flexShrink: 0,
    },
    avatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'contain' as const,
        backgroundColor: '#fff',
    },
    switch: {
        width: '2.75em',
        height: '1.4em',
        cursor: 'pointer',
    },
};
